import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { dropdownSections, DEFAULT_USER_VIEW_PREFERENCES } from '../components/Parcels/propertySettings'
import { isEqual } from 'lodash'
import { applyDefaults, updateParcelPrefsCache } from '../common/utils/UserPreferences'


export const saveParcelPrefs = createAsyncThunk(
  'userProperties/saveParcelPrefs',
  async (newPrefs) => {
    return new Promise(resolve => {
      // eslint-disable-next-line camelcase
      LL.Memory.set({ parcel_detail_preferences: newPrefs }, () => {
        resolve(newPrefs)
      })
    })
  }
)

const initialState = {
  propertySections: {
    ...dropdownSections.reduce((acc, section) => {
      acc[section.id] = section.isOpen
      return acc
    }, {}),
    'dd-parcel-highlights': true, // ParcelHighlights.jsx dropdown
    'dd-other-properties':  false, // ParcelOthers.jsx dropdown
    'dd-additional-items':  true, // Additional Items is dynamically generated
  },
  userPermissions: {
    groupRole:    '',
    isGuest:      null,
    planCssClass: '',
    paidUser:     null,
    inGroup:      null,
    inFree:       null,
  },
  // Image limits for free users (Starter)
  // UsersController#image_limits endpoint returns User.bing_details(@user) which includes:
  imageLimits: {
    allowed:   null,
    remaining: null,
    used:      null,
    total:     null,
    need:      null,
    metered:   null,
  },
  //this will eventually be a hash of bool but empty is default true for all
  parcelPrefs:        {},
  //makes the deselect/select buttons in custom parcel details work
  parcelBulkUpdating: false,
  // lookup limits for free users (Starter)
  // UsersController#lookup_limits endpoint returns User.antiscrape_details(@user) which includes:
  lookupLimits:       {
    allowed:     null,
    remaining:   null,
    used:        null,
    total:       null,
    need:        null,
    metered:     null,
    initialized: false, // To track if it's been fetched from server yet
  }, // requests is status about the async fetches
  requests: {
    lookupLimits: {
      status: null,
      error:  null,
    },
  }
}

// thunk for fetching the user's lookup limits
export const fetchLookupLimits = createAsyncThunk(
  'userProperties/fetchLookupLimits',
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`/users/lookup_limits.json`)

      if(!response.ok) {
        throw new Error(
          `Server responded with ${response.status}: ${response.statusText}`
        )
      }
      const data = await response.json()
      data.initialized = true
      return data

    } catch (error) {
      console.error('Problem getting lookup limits:', error.message)
      return rejectWithValue(error.message)
    }
  }
)

// Reducer and actions
const userPropertiesSlice = createSlice({
  name:     'userProperties',
  initialState,
  reducers: {
    toggleSection: (state, action) => {
      state.propertySections[action.payload] = !state.propertySections[action.payload]
    },
    updateUserPermissions: (state, action) => {
      // Only update if the permissions have changed
      if(!isEqual(state.userPermissions, action.payload)) {
        state.userPermissions = { ...state.userPermissions, ...action.payload }
      }
    },
    setImageLimits: (state, action) => {
      state.imageLimits = action.payload
    },
    setLookupLimits: (state, action) => {
      state.lookupLimits = action.payload
    },
    setParcelPrefs(state, action)  {
      const prefs = action.payload || {}
      // Always apply defaults unless user has declared otherwise
      state.parcelPrefs = applyDefaults(prefs, DEFAULT_USER_VIEW_PREFERENCES)
    },
    setParcelPref(state, action) {
      const { field, value } = action.payload
      state.parcelPrefs[field] = value
    },
    setParcelGroupPrefs(state, action) {
      //obj with bools of fields { parcelnumb: true, address: false}
      Object.assign(state.parcelPrefs, action.payload)
    },
    setParcelBulkUpdating(state, action) {
      state.parcelBulkUpdating = !!action.payload
    },
    resetParcelPrefs(state) {
      state.parcelPrefs = {}
    },
    countParcelLookup: (state) => {
      // eslint-disable-next-line no-use-before-define
      let total = getLookupsTotal(state)
      const limited = typeof(total) === 'number' && !isNaN(total)
      if(limited) {
        state.lookupLimits.remaining -= 1
      } else {
        total = Infinity
      }
      state.lookupLimits.used += 1

      if(state.lookupLimits.used >= total) {
        state.lookupLimits.allowed = false
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLookupLimits.pending, (state) => {
        state.requests.lookupLimits.status = 'loading'
        state.requests.lookupLimits.error = null
        // TODO: redux state for spinner?
      })
      .addCase(fetchLookupLimits.fulfilled, (state, action) => {
        state.lookupLimits = action.payload
        state.requests.lookupLimits.status = 'succeeded'
        state.requests.lookupLimits.error = null
      })
      .addCase(fetchLookupLimits.rejected, (state, action) => {
        state.requests.lookupLimits.status = 'failed'
        state.requests.lookupLimits.error = action.error.message
      })
      .addCase(saveParcelPrefs.fulfilled, (state, action) => {
        const next = action.payload || {}
        const prefsWithDefaults = applyDefaults(next, DEFAULT_USER_VIEW_PREFERENCES)
        state.parcelPrefs = prefsWithDefaults
        updateParcelPrefsCache(prefsWithDefaults)
      })
      .addCase(saveParcelPrefs.rejected, (state, action) => {
        state.parcelPrefsError = action.payload || action.error
      })
      },
})

// Selectors; these total/remaining getters handle the unlimited / null cases and always
// return a number that can be compared
export const getLookupsTotal = (state) => {
  const total = state.userProperties?.lookupLimits?.total
  const limited = typeof(total) === 'number' && !isNaN(total) && total !== Infinity
  return limited ? total : Infinity
}
export const getLookupsRemaining = (state) => {
  const used = state.userProperties?.lookupLimits?.used ?? 0
  const total = getLookupsTotal(state)
  return total - used
}
export const getLookupLimits = (state) => {
  return state.userProperties?.lookupLimits
}
export const isLoadingLookupLimits = (state) => {
  const status = state.userProperties?.requests?.lookupLimits?.status
  return status === 'loading'
}

export const {
  toggleSection,
  updateUserPermissions,
  setImageLimits,
  setLookupLimits,
  setParcelPrefs,
  setParcelPref,
  setParcelGroupPrefs,
  setParcelBulkUpdating,
  countParcelLookup,
  resetParcelPrefs
 } = userPropertiesSlice.actions

export const selectParcelPrefs = rootState => rootState.userProperties.parcelPrefs
export const selectParcelBulk = rootState => rootState.userProperties.parcelBulkUpdating

export default userPropertiesSlice.reducer
